import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  submitSupportRequest,
  getMemberSupportRequests,
  getMenteePairs,
  getPeerConnectionByMember,
  flagConcern,
  getOpenAvailabilitySlots,
  getMemberAppointments,
  bookAppointment,
  cancelAppointment,
  sendAppointmentMessage,
  getAppointmentMessages,
  sendPeerMessage,
  getPeerMessages,
  getNotifications,
  markNotificationRead,
  getSupportNetworkGroups,
  submitGroupJoinRequest,
  getMemberGroupJoinRequests,
} from '../../../api/programmesApi';

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmt12 = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

const isMessagingOpen = (appt) =>
  appt.status === 'confirmed' && Date.now() >= new Date(`${appt.date}T${appt.time}`).getTime();

function supportStatusBadge(status) {
  const map = {
    pending_match: { cls: 'bb-member-badge--yellow', label: 'Pending Review' },
    matched:       { cls: 'bb-member-badge--green',  label: 'Approved. Found Match' },
  };
  const { cls, label } = map[status] || { cls: 'bb-member-badge--gray', label: status };
  return <span className={`bb-member-badge ${cls}`}>{label}</span>;
}

function groupJoinStatusBadge(status) {
  const map = {
    pending:  { cls: 'bb-member-badge--yellow', label: 'Pending Review' },
    approved: { cls: 'bb-member-badge--green',  label: 'Approved. Found Match' },
    declined: { cls: 'bb-member-badge--gray',   label: 'Declined' },
  };
  const { cls, label } = map[status] || { cls: 'bb-member-badge--gray', label: status };
  return <span className={`bb-member-badge ${cls}`}>{label}</span>;
}

const APPT_STATUS = {
  pending_approval: { cls: 'bb-member-badge--yellow', label: 'Awaiting Volunteer' },
  confirmed:        { cls: 'bb-member-badge--green',  label: 'Confirmed' },
  declined:         { cls: 'bb-member-badge--gray',   label: 'Declined' },
  cancelled:        { cls: 'bb-member-badge--gray',   label: 'Cancelled' },
  completed:        { cls: 'bb-member-badge--gray',   label: 'Completed' },
};

const SUPPORT_TYPES = [
  {
    key: 'care_connect',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    title: 'Care & Connect',
    duration: 'Group sessions · 3–6 weeks',
    description: 'Professionally facilitated group sessions bringing together widows in a safe, supportive environment. Share experiences, build connections, and develop coping strategies alongside others who understand your journey.',
    btnLabel: 'Join a Group',
  },
  {
    key: 'rebuild_shine',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5"/>
        <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
        <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
      </svg>
    ),
    title: 'Rebuild & Shine',
    duration: '1-on-1 mentoring · 6–8 months',
    description: 'Be matched with an experienced widow mentor who has walked a similar path. Your mentor will offer guidance, encouragement, and practical wisdom as you navigate life and rebuild your confidence.',
    btnLabel: 'Request a Mentor',
  },
  {
    key: 'widow_lifeline',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        {/* Retro handset receiver silhouette */}
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 11.5a19.79 19.79 0 0 1-3.07-8.68A2 2 0 0 1 3.8 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.14 6.14l1.1-1.12a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
    title: 'Widow-to-Widow Life Line',
    duration: 'On-demand peer connection',
    description: 'Connect with a peer who truly understands your journey. When you need someone to talk to — someone who gets it — our peer connection network is here for you.',
    btnLabel: 'Request Connection',
  },
];

// ── Small (20 px) section-header icons — same paths as SUPPORT_TYPES cards ────
const SECTION_ICONS = {
  care_connect: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  rebuild_shine: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  widow_lifeline: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 11.5a19.79 19.79 0 0 1-3.07-8.68A2 2 0 0 1 3.8 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.14 6.14l1.1-1.12a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
};

// ── In-App Appointment Chat ────────────────────────────────────────────────────
function AppointmentChat({ orgId, appointmentId, memberId, memberName, volunteerFirstName }) {
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [sending, setSending]   = useState(false);
  const bottomRef               = useRef(null);

  const loadMessages = useCallback(() => {
    getAppointmentMessages(orgId, appointmentId).then(setMessages).catch(() => {});
  }, [orgId, appointmentId]);

  useEffect(() => {
    loadMessages();
    const iv = setInterval(loadMessages, 8000);
    return () => clearInterval(iv);
  }, [loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await sendAppointmentMessage(orgId, appointmentId, memberId, memberName, 'member', text.trim());
      setText('');
      loadMessages();
    } catch { /* silent */ } finally { setSending(false); }
  };

  return (
    <div style={{ marginTop: 12, border: '1px solid #99F6E4', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ background: '#0D9488', color: '#fff', padding: '8px 14px', fontSize: 12, fontWeight: 700 }}>
        💬 Secure Chat — with {volunteerFirstName}
        <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.8 }}>No contact info shared</span>
      </div>
      <div style={{ height: 220, overflowY: 'auto', padding: '10px 14px', background: '#F0FDFA' }}>
        {messages.length === 0 && (
          <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', marginTop: 20 }}>
            No messages yet. Say hello!
          </div>
        )}
        {messages.map((m) => {
          const mine = m.senderId === memberId;
          return (
            <div key={m.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
              <div style={{
                display: 'inline-block',
                background: mine ? '#0D9488' : '#E5E7EB',
                color: mine ? '#fff' : '#1F2937',
                padding: '7px 12px', borderRadius: 12,
                maxWidth: '75%', fontSize: 13, lineHeight: 1.4,
              }}>
                {m.text}
              </div>
              <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                {mine ? 'You' : volunteerFirstName} ·{' '}
                {m.createdAt?.toDate ? m.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '8px 10px', borderTop: '1px solid #CCFBF1', background: '#fff' }}>
        <input
          className="bb-input"
          style={{ flex: 1, fontSize: 13 }}
          placeholder="Type a message…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
        />
        <button
          className="bb-member-btn bb-member-btn-primary bb-member-btn-sm"
          style={{ background: '#0D9488', border: 'none', color: '#fff' }}
          onClick={handleSend}
          disabled={sending || !text.trim()}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// ── Peer Chat (Widow-to-Widow) ─────────────────────────────────────────────────
function PeerChat({ orgId, connectionId, memberId, memberName }) {
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [sending, setSending]   = useState(false);
  const bottomRef               = useRef(null);

  const loadMessages = useCallback(() => {
    getPeerMessages(orgId, connectionId).then(setMessages).catch(() => {});
  }, [orgId, connectionId]);

  useEffect(() => {
    loadMessages();
    const iv = setInterval(loadMessages, 10000);
    return () => clearInterval(iv);
  }, [loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await sendPeerMessage(orgId, connectionId, memberId, memberName, 'member', text.trim());
      setText('');
      loadMessages();
    } catch { } finally { setSending(false); }
  };

  return (
    <div style={{ marginTop: 14, border: '1px solid #99F6E4', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ background: '#0D9488', color: '#fff', padding: '8px 14px', fontSize: 12, fontWeight: 700 }}>
        💬 Message your Befriender
        <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.8 }}>No contact details shared</span>
      </div>
      <div style={{ height: 200, overflowY: 'auto', padding: '10px 14px', background: '#F0FDFA' }}>
        {messages.length === 0 && (
          <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', marginTop: 20 }}>
            No messages yet. Say hello to your Befriender!
          </div>
        )}
        {messages.map(m => {
          const mine = m.senderId === memberId;
          return (
            <div key={m.id} style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
              <div style={{ background: mine ? '#0D9488' : '#E5E7EB', color: mine ? '#fff' : '#1F2937', padding: '7px 12px', borderRadius: 12, maxWidth: '75%', fontSize: 13, lineHeight: 1.4 }}>
                {m.text}
              </div>
              <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                {mine ? 'You' : 'Befriender'} ·{' '}
                {m.createdAt?.toDate ? m.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '8px 10px', borderTop: '1px solid #CCFBF1', background: '#fff' }}>
        <input
          className="bb-input"
          style={{ flex: 1, fontSize: 13 }}
          placeholder="Type a message…"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
        />
        <button
          className="bb-member-btn bb-member-btn-primary bb-member-btn-sm"
          style={{ background: '#0D9488', border: 'none', color: '#fff' }}
          onClick={handleSend}
          disabled={sending || !text.trim()}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

// ── Support Options Tab ────────────────────────────────────────────────────────
function SupportOptions({ orgId, memberId, memberName, onGoToConnections }) {
  const [requests, setRequests]           = useState([]);
  const [submitting, setSubmitting]       = useState({});
  const [toast, setToast]                 = useState('');
  const [loading, setLoading]             = useState(true);

  // Group browsing
  const [availableGroups, setAvailableGroups] = useState([]);
  const [joinRequests, setJoinRequests]       = useState([]);
  const [showGroupBrowser, setShowGroupBrowser] = useState(false);
  const [joiningGroup, setJoiningGroup]       = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        if (memberId) {
          const [data, groups, joinReqs] = await Promise.all([
            getMemberSupportRequests(orgId, memberId),
            getSupportNetworkGroups(orgId).catch(() => []),
            getMemberGroupJoinRequests(orgId, memberId).catch(() => []),
          ]);
          setRequests(data);
          setAvailableGroups(groups);
          setJoinRequests(joinReqs);
        }
      } catch (err) {
        console.error('Error loading support requests:', err);
      } finally { setLoading(false); }
    };
    load();
  }, [orgId, memberId]);

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(''), 3500);
  };

  const handleRequest = async (typeKey) => {
    setSubmitting((prev) => ({ ...prev, [typeKey]: true }));
    try {
      await submitSupportRequest(orgId, memberId, memberName, typeKey);
      const data = await getMemberSupportRequests(orgId, memberId);
      setRequests(data);
      showToast('Your request has been submitted. A caseworker will be in touch soon.');
    } catch (err) {
      showToast(err.message || 'Error submitting request.', true);
    } finally { setSubmitting((prev) => ({ ...prev, [typeKey]: false })); }
  };

  const handleJoinGroup = async (group) => {
    setJoiningGroup(p => ({ ...p, [group.id]: true }));
    try {
      await submitGroupJoinRequest(orgId, memberId, memberName, group.id, group.name);
      const joinReqs = await getMemberGroupJoinRequests(orgId, memberId);
      setJoinRequests(joinReqs);
      showToast(`Request to join "${group.name}" submitted! You'll be notified once reviewed.`);
    } catch (err) {
      showToast(err.message || 'Failed to submit request.', true);
    } finally { setJoiningGroup(p => ({ ...p, [group.id]: false })); }
  };

  // Helpers for group browser
  const getGroupRequestStatus = (groupId) => {
    const req = joinRequests.find(r => r.groupId === groupId);
    return req ? req.status : null;
  };

  const requestByType = (typeKey) =>
    requests.filter((r) => r.type === typeKey).sort((a, b) => {
      const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const db = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return db - da;
    })[0] || null;

  if (loading) return (
    <div className="bb-empty-state">
      <div className="bb-loading-spinner"></div>
      <p>Loading support options...</p>
    </div>
  );

  return (
    <div>
      <h2 className="bb-member-section-title">Support Options</h2>
      <p className="bb-member-section-sub">Choose the type of support that is right for you. You can request more than one.</p>

      <div className="bb-member-support-cards">
        {SUPPORT_TYPES.map((support) => {
          const existing      = requestByType(support.key);
          const hasActive     = existing && ['active', 'matched'].includes(existing.status);
          const hasPending    = existing && existing.status === 'pending_match';
          const isCareConnect = support.key === 'care_connect';

          // For Care & Connect, find the latest non-declined group join request
          const activeJoinReq = isCareConnect
            ? (joinRequests.find(r => r.status === 'pending' || r.status === 'approved') || null)
            : null;

          // Card is navigable when the match/approval is confirmed.
          // Scroll key is always support.key with underscores replaced by hyphens —
          // any new SUPPORT_TYPES entry is automatically covered.
          const scrollKey  = support.key.replace(/_/g, '-');
          const isApproved = isCareConnect
            ? activeJoinReq?.status === 'approved'
            : hasActive;

          return (
            <div
              key={support.key}
              className="bb-member-support-card"
              onClick={isApproved ? () => onGoToConnections(scrollKey) : undefined}
              style={isApproved ? { cursor: 'pointer' } : undefined}
              title={isApproved ? 'Click to view your connection' : undefined}
            >
              <div className="bb-member-support-card-icon">{support.icon}</div>
              <div className="bb-member-support-card-title">{support.title}</div>
              <div className="bb-member-support-card-duration">{support.duration}</div>
              <div className="bb-member-support-card-desc">{support.description}</div>
              {existing && !isCareConnect && (
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#64748B' }}>Your request:</span>
                  {supportStatusBadge(existing.status)}
                </div>
              )}
              {isCareConnect && activeJoinReq && (
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#64748B' }}>Your request:</span>
                  {groupJoinStatusBadge(activeJoinReq.status)}
                </div>
              )}
              {isCareConnect ? (
                activeJoinReq ? (
                  <button
                    className="bb-member-btn bb-member-btn-sm"
                    disabled
                    style={{ opacity: 0.65, cursor: 'not-allowed' }}
                  >
                    Request Submitted
                  </button>
                ) : (
                  <button
                    className="bb-member-btn bb-member-btn-primary bb-member-btn-sm"
                    onClick={() => setShowGroupBrowser(v => !v)}
                  >
                    {showGroupBrowser ? 'Hide Groups' : support.btnLabel}
                  </button>
                )
              ) : (hasActive || hasPending) ? (
                <button
                  className="bb-member-btn bb-member-btn-sm"
                  disabled
                  style={{ opacity: 0.65, cursor: 'not-allowed' }}
                >
                  Request Submitted
                </button>
              ) : (
                <button
                  className="bb-member-btn bb-member-btn-primary bb-member-btn-sm"
                  onClick={() => handleRequest(support.key)}
                  disabled={submitting[support.key]}
                >
                  {submitting[support.key] ? 'Submitting...' : support.btnLabel}
                </button>
              )}
              {isApproved && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#0D9488', fontWeight: 600 }}>
                  View your connection →
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Group Browser ── */}
      {showGroupBrowser && (
        <div style={{ marginTop: 24, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1F2937', marginBottom: 4 }}>Available Support Groups</div>
          <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px' }}>
            Browse groups and request to join one. An admin will review your request and notify you.
          </p>
          {availableGroups.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', padding: '20px 0' }}>
              No groups available at the moment.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {availableGroups.map(g => {
                const reqStatus = getGroupRequestStatus(g.id);
                const rec = g.isRecurring && g.recurrence;
                const recLabel = (() => {
                  if (!rec) return null;
                  if (rec.frequency === 'weekly') {
                    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                    return `Weekly · ${(rec.daysOfWeek || []).map(d => days[d]).join(', ')}`;
                  }
                  if (rec.frequency === 'monthly') return `Monthly · day ${rec.dayOfMonth}`;
                  if (rec.frequency === 'quarterly') return 'Quarterly';
                  return rec.frequency;
                })();
                return (
                  <div key={g.id} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1F2937', marginBottom: 4 }}>{g.name}</div>
                      {g.cohortName && <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>{g.cohortName}</div>}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: '#475569' }}>
                        {g.facilitatorName && <span>👤 {g.facilitatorName}</span>}
                        {recLabel && <span>🔁 {recLabel}</span>}
                        {g.meetingVenue && <span>📍 {g.meetingVenue}</span>}
                        {g.district && <span>🗺 {g.district}</span>}
                        {g.capacity && <span>👥 Capacity: {g.capacity}</span>}
                      </div>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      {reqStatus === 'pending' && (
                        <span style={{ display: 'inline-block', background: '#FEF3C7', color: '#92400E', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6 }}>
                          ⏳ Request Pending
                        </span>
                      )}
                      {reqStatus === 'approved' && (
                        <span style={{ display: 'inline-block', background: '#D1FAE5', color: '#065F46', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6 }}>
                          ✅ Approved
                        </span>
                      )}
                      {reqStatus === 'declined' && (
                        <span style={{ display: 'inline-block', background: '#FEE2E2', color: '#991B1B', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6 }}>
                          ✗ Not Approved
                        </span>
                      )}
                      {!reqStatus && (
                        <button
                          className="bb-member-btn bb-member-btn-primary bb-member-btn-sm"
                          disabled={joiningGroup[g.id]}
                          onClick={() => handleJoinGroup(g)}
                          style={{ fontSize: 12 }}
                        >
                          {joiningGroup[g.id] ? 'Requesting…' : 'Request to Join'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {toast && <div className={`bb-member-toast${toast.isError ? ' error' : ''}`}>{toast.msg}</div>}
    </div>
  );
}

// ── My Connections Tab ─────────────────────────────────────────────────────────
function MyConnections({ orgId, memberId, memberName, scrollTarget }) {
  const [requests, setRequests]       = useState([]);
  const [mentorPair, setMentorPair]   = useState(null);
  const [peerConn, setPeerConn]       = useState(null);
  const [openSlots, setOpenSlots]     = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]         = useState(true);

  // Single mutable map — any section registers itself with a callback ref using
  // the key derived from support.key.replace(/_/g, '-'). No changes needed here
  // when new support types are added to SUPPORT_TYPES.
  const sectionRefsMap = useRef({});
  const [booking, setBooking]         = useState({});
  const [cancelling, setCancelling]   = useState({});
  const [openChat, setOpenChat]       = useState(null);
  const [showConcernForm, setShowConcernForm] = useState(false);
  const [concern, setConcern]         = useState({ type: 'safety', description: '' });
  const [submittingConcern, setSubmittingConcern] = useState(false);
  const [concernSent, setConcernSent] = useState(false);
  const [toast, setToast]             = useState('');
  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(''), 3500);
  };

  const [groupJoinReqs, setGroupJoinReqs] = useState([]);

  const load = useCallback(async () => {
    try {
      const [reqData, mentorData, peerData, slots, appts, notifs, joinReqs] = await Promise.all([
        getMemberSupportRequests(orgId, memberId),
        getMenteePairs(orgId, memberId),
        getPeerConnectionByMember(orgId, memberId),
        getOpenAvailabilitySlots(orgId),
        getMemberAppointments(orgId, memberId),
        getNotifications(orgId, memberId),
        getMemberGroupJoinRequests(orgId, memberId).catch(() => []),
      ]);
      setRequests(reqData);
      setMentorPair(mentorData?.[0] || null);
      setPeerConn(peerData);
      setOpenSlots(slots);
      setAppointments(appts);
      setNotifications(notifs);
      setGroupJoinReqs(joinReqs);
    } catch (err) {
      console.error('Error loading connections:', err);
    } finally { setLoading(false); }
  }, [orgId, memberId]);

  useEffect(() => { if (memberId) load(); }, [load, memberId]);

  // Re-check every 60 s so the LIVE badge and chat window auto-appear at appointment time
  useEffect(() => {
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  // Scroll to the relevant section once data has loaded
  useEffect(() => {
    if (loading || !scrollTarget) return;
    const el = sectionRefsMap.current[scrollTarget];
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [loading, scrollTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (notifId) => {
    await markNotificationRead(orgId, notifId).catch(() => {});
    setNotifications(ns => ns.map(n => n.id === notifId ? { ...n, read: true } : n));
  };

  const handleBook = async (slot) => {
    setBooking(b => ({ ...b, [slot.id]: true }));
    try {
      await bookAppointment(orgId, slot.id, slot.volunteerId, slot.volunteerName, memberId, memberName, slot.date, slot.time, slot.duration);
      showToast(`Appointment request sent to ${slot.volunteerName.split(' ')[0]}. You'll be notified when they respond.`);
      load();
    } catch (err) {
      showToast(err.message || 'Error booking appointment.', true);
    } finally { setBooking(b => ({ ...b, [slot.id]: false })); }
  };

  const handleCancel = async (appointmentId) => {
    if (!window.confirm('Cancel this appointment request?')) return;
    setCancelling(c => ({ ...c, [appointmentId]: true }));
    try {
      await cancelAppointment(orgId, appointmentId, memberId);
      showToast('Appointment cancelled.');
      load();
    } catch { showToast('Error cancelling appointment.', true); }
    finally { setCancelling(c => ({ ...c, [appointmentId]: false })); }
  };

  const handleFlagConcern = async (e) => {
    e.preventDefault();
    if (!concern.description.trim()) return;
    setSubmittingConcern(true);
    try {
      await flagConcern(orgId, memberId, memberName, 'member', concern.type, concern.description, memberId, memberName);
      setConcernSent(true);
      setConcern({ type: 'safety', description: '' });
      setShowConcernForm(false);
      showToast('Your concern has been flagged. A caseworker will follow up with you.');
    } catch (err) {
      showToast(err.message || 'Error submitting concern.', true);
    } finally { setSubmittingConcern(false); }
  };

  // Derive per-type requests from the loaded support requests
  const rebuildReq = requests.find(r => r.type === 'rebuild_shine');
  const widowReq   = requests.find(r => r.type === 'widow_lifeline');
  const hasAnyConnection = groupJoinReqs.length > 0 || mentorPair || rebuildReq || peerConn || widowReq;

  const unreadNotifs = notifications.filter(n => !n.read);

  // Group open slots by volunteer
  const slotsByVolunteer = openSlots.reduce((acc, s) => {
    if (!acc[s.volunteerId]) acc[s.volunteerId] = { name: s.volunteerName, slots: [] };
    acc[s.volunteerId].slots.push(s);
    return acc;
  }, {});

  // Filter out slots the member already has a pending/confirmed appointment for
  const bookedSlotIds = new Set(
    appointments.filter(a => ['pending_approval', 'confirmed'].includes(a.status)).map(a => a.availabilityId)
  );

  const activeAppts    = appointments.filter(a => ['pending_approval', 'confirmed'].includes(a.status));
  const pastAppts      = appointments.filter(a => ['declined', 'cancelled', 'completed'].includes(a.status));

  if (loading) return (
    <div className="bb-empty-state">
      <div className="bb-loading-spinner"></div>
      <p>Loading your connections...</p>
    </div>
  );

  return (
    <div>
      <h2 className="bb-member-section-title">My Connections</h2>
      <p className="bb-member-section-sub">Your active support connections, scheduled appointments, and secure messaging.</p>

      {/* Notifications */}
      {unreadNotifs.length > 0 && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#92400E', marginBottom: 8 }}>
            🔔 {unreadNotifs.length} new notification{unreadNotifs.length > 1 ? 's' : ''}
          </div>
          {unreadNotifs.map(n => (
            <div key={n.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#78350F', marginBottom: 6 }}>
              <span>{n.message}</span>
              <button className="bb-member-btn bb-member-btn-outline bb-member-btn-sm" style={{ fontSize: 10, marginLeft: 10 }} onClick={() => markRead(n.id)}>
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ── */}
      {!hasAnyConnection && (
        <div className="bb-empty-state" style={{ marginBottom: 24 }}>
          <p>You don't have any active programme connections yet. Visit <strong>Support Options</strong> to get started.</p>
        </div>
      )}

      {/* ══ Care & Connect ══════════════════════════════════════════════════════ */}
      {groupJoinReqs.length > 0 && (
        <div ref={el => { sectionRefsMap.current['care-connect'] = el; }} style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #CCFBF1' }}>
            <div style={{ color: '#0F766E', lineHeight: 1 }}>{SECTION_ICONS.care_connect}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>Care &amp; Connect</div>
              <div style={{ fontSize: 12, color: '#6B7280' }}>Group sessions · 3–6 weeks</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {groupJoinReqs.map(req => (
              <div key={req.id} style={{
                background: req.status === 'approved' ? '#F0FDF4' : req.status === 'declined' ? '#FEF2F2' : '#FFFBEB',
                border: `1px solid ${req.status === 'approved' ? '#BBF7D0' : req.status === 'declined' ? '#FECACA' : '#FDE68A'}`,
                borderRadius: 8, padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937', marginBottom: 4 }}>{req.groupName}</div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                      Requested {req.requestedAt?.toDate
                        ? req.requestedAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </div>
                    {req.status === 'declined' && req.declineReason && (
                      <div style={{ fontSize: 12, color: '#991B1B', marginTop: 4 }}>Reason: {req.declineReason}</div>
                    )}
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {req.status === 'pending' && (
                      <span style={{ background: '#FEF3C7', color: '#92400E', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 6 }}>⏳ Pending Review</span>
                    )}
                    {req.status === 'approved' && (
                      <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 6 }}>✅ Approved — You're in!</span>
                    )}
                    {req.status === 'declined' && (
                      <span style={{ background: '#FEE2E2', color: '#991B1B', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 6 }}>✗ Not Approved</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ Rebuild & Shine ════════════════════════════════════════════════════ */}
      {(mentorPair || rebuildReq) && (
        <div ref={el => { sectionRefsMap.current['rebuild-shine'] = el; }} style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #E0E7FF' }}>
            <div style={{ color: '#0F766E', lineHeight: 1 }}>{SECTION_ICONS.rebuild_shine}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>Rebuild &amp; Shine</div>
              <div style={{ fontSize: 12, color: '#6B7280' }}>1-on-1 mentoring · 6–8 months</div>
            </div>
          </div>
          {mentorPair ? (
            <div className="bb-member-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937' }}>
                    Mentor:{' '}
                    <strong>{mentorPair.mentorName ? mentorPair.mentorName.split(' ')[0] : 'Your mentor'}</strong>
                    <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 6 }}>(first name only for privacy)</span>
                  </div>
                  {mentorPair.startDate && (
                    <div style={{ fontSize: 13, color: '#64748B', marginTop: 4 }}>Started: {formatDate(mentorPair.startDate)}</div>
                  )}
                </div>
                <span className="bb-member-badge bb-member-badge--green">Active</span>
              </div>
              {mentorPair.mentorBio && (
                <div style={{ fontSize: 13, color: '#475569', marginTop: 8, fontStyle: 'italic' }}>"{mentorPair.mentorBio}"</div>
              )}
            </div>
          ) : (
            <div className="bb-member-card" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937', marginBottom: 6 }}>Mentor Match Requested</div>
                  <div style={{ fontSize: 13, color: '#78350F', lineHeight: 1.5 }}>
                    Your request is being reviewed. A caseworker will match you with a suitable mentor.
                  </div>
                  {rebuildReq?.createdAt && (
                    <div style={{ fontSize: 12, color: '#92400E', marginTop: 6 }}>Requested: {formatDate(rebuildReq.createdAt)}</div>
                  )}
                </div>
                <span style={{ background: '#FEF3C7', color: '#92400E', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 6, flexShrink: 0 }}>
                  ⏳ Pending Review
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ Widow-to-Widow Life Line ════════════════════════════════════════════ */}
      {(peerConn || widowReq) && (
        <div ref={el => { sectionRefsMap.current['widow-lifeline'] = el; }} style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #CCFBF1' }}>
            <div style={{ color: '#0F766E', lineHeight: 1 }}>{SECTION_ICONS.widow_lifeline}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>Widow-to-Widow Life Line</div>
              <div style={{ fontSize: 12, color: '#6B7280' }}>On-demand peer connection</div>
            </div>
          </div>
          {peerConn ? (
            <div className="bb-member-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937' }}>
                    Befriender:{' '}
                    <strong>{peerConn.supporterName ? peerConn.supporterName.split(' ')[0] : 'Your Befriender'}</strong>
                    <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 6 }}>(first name only for privacy)</span>
                  </div>
                  {peerConn.startDate && (
                    <div style={{ fontSize: 13, color: '#64748B', marginTop: 4 }}>Connected since: {formatDate(peerConn.startDate)}</div>
                  )}
                </div>
                <span className="bb-member-badge bb-member-badge--teal">Active</span>
              </div>
              <PeerChat orgId={orgId} connectionId={peerConn.id} memberId={memberId} memberName={memberName} />
            </div>
          ) : (
            <div className="bb-member-card" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937', marginBottom: 6 }}>Befriender Connection Requested</div>
                  <div style={{ fontSize: 13, color: '#78350F', lineHeight: 1.5 }}>
                    Your request is being reviewed. A caseworker will connect you with a befriender who understands your journey.
                  </div>
                  {widowReq?.createdAt && (
                    <div style={{ fontSize: 12, color: '#92400E', marginTop: 6 }}>Requested: {formatDate(widowReq.createdAt)}</div>
                  )}
                </div>
                <span style={{ background: '#FEF3C7', color: '#92400E', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 6, flexShrink: 0 }}>
                  ⏳ Pending Review
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Book an Appointment ── */}
      <div style={{ marginTop: 28, marginBottom: 28 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0D9488', marginBottom: 6 }}>Book an Appointment</h3>
        <p style={{ fontSize: 13, color: '#64748B', marginBottom: 14 }}>
          Volunteers who have opened availability are listed below. Choose a time that works for you — your request will be sent to the volunteer for approval.
        </p>

        {Object.keys(slotsByVolunteer).length === 0 ? (
          <div className="bb-empty-state" style={{ fontSize: 13 }}>
            No volunteers have open appointment slots at the moment. Check back soon.
          </div>
        ) : (
          Object.entries(slotsByVolunteer).map(([volId, { name, slots }]) => (
            <div key={volId} className="bb-member-card" style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#CCFBF1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: '#0D9488' }}>
                  {name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0F766E' }}>{name}</div>
                  <div style={{ fontSize: 11, color: '#64748B' }}>Volunteer · {slots.length} slot{slots.length !== 1 ? 's' : ''} available</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {slots.map(slot => {
                  const alreadyBooked = bookedSlotIds.has(slot.id);
                  return (
                    <div key={slot.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F0FDFA', borderRadius: 8, padding: '8px 12px' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13, color: '#1F2937' }}>{fmtDate(slot.date)}</span>
                        <span style={{ fontSize: 13, color: '#475569', marginLeft: 8 }}>{fmt12(slot.time)}</span>
                        <span style={{ fontSize: 12, color: '#64748B', marginLeft: 8 }}>· {slot.duration} min</span>
                      </div>
                      <button
                        className="bb-member-btn bb-member-btn-primary bb-member-btn-sm"
                        style={{ background: '#0D9488', border: 'none', color: '#fff', opacity: alreadyBooked ? 0.5 : 1 }}
                        disabled={booking[slot.id] || alreadyBooked}
                        onClick={() => handleBook(slot)}
                      >
                        {booking[slot.id] ? 'Booking…' : alreadyBooked ? 'Requested' : 'Book'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── My Appointments ── */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0D9488', marginBottom: 10 }}>My Appointments</h3>

        {activeAppts.length === 0 && pastAppts.length === 0 ? (
          <div className="bb-empty-state" style={{ fontSize: 13 }}>No appointments yet. Book one above.</div>
        ) : (
          <>
            {activeAppts.map(appt => {
              const chatOpen = openChat === appt.id;
              const messaging = isMessagingOpen(appt);
              const badge = APPT_STATUS[appt.status] || { cls: 'bb-member-badge--gray', label: appt.status };
              return (
                <div key={appt.id} className="bb-member-card" style={{ marginBottom: 14, borderLeft: `3px solid ${messaging ? '#0D9488' : '#E5E7EB'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1F2937' }}>
                        Appointment with {appt.volunteerName}
                      </div>
                      <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
                        {fmtDate(appt.date)} · {fmt12(appt.time)} · {appt.duration} min
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span className={`bb-member-badge ${badge.cls}`}>{badge.label}</span>
                      {messaging && (
                        <span style={{ background: '#0D9488', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>
                          LIVE
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {messaging ? (
                      <button
                        className="bb-member-btn bb-member-btn-primary bb-member-btn-sm"
                        style={{ background: '#0D9488', border: 'none', color: '#fff' }}
                        onClick={() => setOpenChat(chatOpen ? null : appt.id)}
                      >
                        💬 {chatOpen ? 'Close Chat' : 'Open Chat'}
                      </button>
                    ) : appt.status === 'confirmed' ? (
                      <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>Chat opens at appointment time</span>
                    ) : null}

                    {appt.status === 'pending_approval' && (
                      <button
                        className="bb-member-btn bb-member-btn-outline bb-member-btn-sm"
                        style={{ color: '#DC2626', borderColor: '#DC2626', fontSize: 12 }}
                        disabled={cancelling[appt.id]}
                        onClick={() => handleCancel(appt.id)}
                      >
                        {cancelling[appt.id] ? 'Cancelling…' : 'Cancel Request'}
                      </button>
                    )}
                  </div>

                  {chatOpen && messaging && (
                    <AppointmentChat
                      orgId={orgId}
                      appointmentId={appt.id}
                      memberId={memberId}
                      memberName={memberName}
                      volunteerFirstName={appt.volunteerName?.split(' ')[0] || 'Volunteer'}
                    />
                  )}
                </div>
              );
            })}

            {pastAppts.length > 0 && (
              <details style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Past appointments ({pastAppts.length})
                </summary>
                {pastAppts.map(appt => {
                  const badge = APPT_STATUS[appt.status] || { cls: 'bb-member-badge--gray', label: appt.status };
                  return (
                    <div key={appt.id} style={{ padding: '8px 0', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: 13, color: '#1F2937' }}>{appt.volunteerName}</span>
                        <span style={{ fontSize: 12, color: '#64748B', marginLeft: 8 }}>{fmtDate(appt.date)} · {fmt12(appt.time)}</span>
                      </div>
                      <span className={`bb-member-badge ${badge.cls}`} style={{ fontSize: 11 }}>{badge.label}</span>
                    </div>
                  );
                })}
              </details>
            )}
          </>
        )}
      </div>

      {/* ── Flag a Concern ── */}
      <div>
        {!showConcernForm && !concernSent && (
          <button className="bb-member-btn bb-member-btn-outline bb-member-btn-sm" onClick={() => setShowConcernForm(true)}>
            Flag a Concern
          </button>
        )}
        {concernSent && (
          <div className="bb-member-info-box teal" style={{ marginTop: 8 }}>
            Your concern has been submitted. A caseworker will follow up with you confidentially.
          </div>
        )}
        {showConcernForm && (
          <form className="bb-member-concern-form" onSubmit={handleFlagConcern}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#92400E', marginBottom: 12 }}>Flag a Concern</div>
            <div className="bb-form-group">
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Type of concern</label>
              <select className="bb-select" value={concern.type} onChange={e => setConcern({ ...concern, type: e.target.value })} style={{ width: '100%', padding: '7px 10px', fontSize: 13 }}>
                <option value="safety">Safety concern</option>
                <option value="inappropriate_behaviour">Inappropriate behaviour</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="bb-form-group" style={{ marginTop: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Describe your concern</label>
              <textarea className="bb-textarea" rows={4} value={concern.description} onChange={e => setConcern({ ...concern, description: e.target.value })} placeholder="Please describe what happened..." required style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button type="submit" className="bb-member-btn bb-member-btn-primary bb-member-btn-sm" disabled={submittingConcern}>
                {submittingConcern ? 'Submitting...' : 'Submit Concern'}
              </button>
              <button type="button" className="bb-member-btn bb-member-btn-outline bb-member-btn-sm" onClick={() => setShowConcernForm(false)}>Cancel</button>
            </div>
          </form>
        )}
      </div>

      {toast && <div className={`bb-member-toast${toast.isError ? ' error' : ''}`}>{toast.msg}</div>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function SupportNetworkMember({ orgId, memberId, memberName, activeTab, setActiveTab }) {
  const [scrollTarget, setScrollTarget] = useState(null);

  const handleGoToConnections = useCallback((target) => {
    setScrollTarget(target);
    setActiveTab('My Connections');
  }, [setActiveTab]);

  return (
    <>
      {activeTab === 'Support Options' && (
        <SupportOptions
          orgId={orgId}
          memberId={memberId}
          memberName={memberName}
          onGoToConnections={handleGoToConnections}
        />
      )}
      {activeTab === 'My Connections' && (
        <MyConnections
          orgId={orgId}
          memberId={memberId}
          memberName={memberName}
          scrollTarget={scrollTarget}
        />
      )}
    </>
  );
}

export default SupportNetworkMember;
